import React, { useState, useEffect } from 'react';
import { DialogContent, Input, Select, SelectChangeEvent, useFlashMessage } from '@indigo-ag/component-module';
import i18next from 'core/i18n';
import {
  AgFinancingFile,
  AgFinancingEvaluationType,
  AgFinancingFilesContext,
  AgFinancingFileInput,
  Phase1Input
} from '@indigo-ag/schema';

import { EnhancedDialog, UploadDocument, IIbamaInfo, evaluationTypesOptions, ModalActionButtons } from 'shared';
import {
  agFinancingFilesToFiles,
  ProgressDictionary,
  removeFilesFromAgFinancing,
  updateFilesAgFinancingFiles,
  uploadFilesToAgFinancing
} from '../../../../../utils';
import { ApolloError } from '@apollo/client';

export interface BackgroundCheckModalIbamaProps {
  closeModal: () => void;
  open: boolean;
  ibamaInfo?: IIbamaInfo;
  onSave: (newPhaseInfo: Partial<Phase1Input>) => Promise<unknown>;
  toUploadFiles: ReturnType<typeof uploadFilesToAgFinancing>;
}

export const BackgroundCheckModalIbama: React.FunctionComponent<BackgroundCheckModalIbamaProps> = ({
  closeModal,
  open,
  ibamaInfo,
  onSave,
  toUploadFiles
}) => {
  /**
   * State
   */
  const [allFiles, setAllFiles] = useState<Partial<AgFinancingFile>[]>(ibamaInfo?.ibamaFiles || []);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [filesIdsToRemove, setFilesIdsToRemove] = useState<AgFinancingFile['id'][]>([]);
  const [note, setNote] = useState(ibamaInfo?.ibamaNotes || '');
  const [option, setOption] = useState<AgFinancingEvaluationType | null>(ibamaInfo?.ibamaEvaluation || null);
  const [progress, setProgress] = useState<ProgressDictionary>();
  const [filesUpdated, setFilesUpdated] = useState<AgFinancingFile[]>([]);

  const onCleanState = () => {
    setAllFiles(ibamaInfo?.ibamaFiles || []);
    setNewFiles([]);
    setFilesIdsToRemove([]);
    setNote(ibamaInfo?.ibamaNotes || '');
    setOption(ibamaInfo?.ibamaEvaluation || null);
    setFilesUpdated([]);
  };

  useEffect(() => {
    onCleanState();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setNote(event.target.value);
  };

  const handleChange = (event: SelectChangeEvent<AgFinancingEvaluationType>): void => setOption(event.target.value);

  const addNewFiles = (files: File[]) => {
    setNewFiles([...newFiles, ...files]);

    setAllFiles([
      ...allFiles,
      ...files.map(f => ({
        ...f,
        documentName: f.name
      }))
    ]);
  };

  const fileReplacer = (file: File) => {
    const fileIndex = newFiles.findIndex(f => f['newFileId'] === file['newFileId']);
    const replacedFiles = [...newFiles];
    replacedFiles[fileIndex]['comment'] = file['comment'];
    setNewFiles(replacedFiles);
  };

  const onUpdateFile = (file: File) => {
    updateFilesAgFinancingFiles(file, allFiles, filesUpdated, setFilesUpdated);
  };

  const addFileToRemove = (fileIdx: number) => {
    const updatedIbamaFiles = [...allFiles];
    const fileToRemove = updatedIbamaFiles.splice(fileIdx, 1)[0];

    if (fileToRemove.id) {
      setFilesIdsToRemove([...filesIdsToRemove, fileToRemove.id]);
    } else {
      const updatedNewFiles = [...newFiles];
      const newFileToRemoveIdx = fileIdx - (ibamaInfo?.ibamaFiles ? ibamaInfo.ibamaFiles.length : 0);
      updatedNewFiles.splice(newFileToRemoveIdx, 1);
      setNewFiles(updatedNewFiles);
    }

    setAllFiles(updatedIbamaFiles);
  };

  const { addFlashMessage } = useFlashMessage();

  const updateIbamaInfo = () =>
    removeFilesFromAgFinancing(filesIdsToRemove)
      .then(() =>
        toUploadFiles(newFiles, AgFinancingFilesContext.IBAMA, undefined, progressCompleted =>
          setProgress(progressCompleted)
        )
      )
      .then(filesUploaded =>
        onSave({
          ibamaNotes: note,
          ibamaEvaluation: option || undefined,
          files: filesUploaded.fulfilled as AgFinancingFileInput[]
        })
      )
      .then(() => {
        addFlashMessage({
          content: `Ibama ${i18next.t('hasBeenUpdatedSuccessfully')}`,
          title: '',
          variant: 'success'
        });
        setProgress({});
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });

  return (
    <EnhancedDialog open={open} onClose={closeModal} title={'Ibama'}>
      <DialogContent>
        <Select
          label={i18next.t('evaluation')}
          name="evaluation-options"
          options={evaluationTypesOptions}
          onChange={handleChange}
          value={option}
          placeholder={i18next.t('pleaseSelect')}
        />
        <Input label={`${i18next.t('notes')} (${i18next.t('optional')})`} value={note} onChange={handleInputChange} />
        <UploadDocument
          files={agFinancingFilesToFiles(allFiles)}
          onRemoveFile={addFileToRemove}
          onNewFiles={addNewFiles}
          progress={progress}
          replaceItem={fileReplacer}
          onUpdateFile={onUpdateFile}
        />
      </DialogContent>
      <ModalActionButtons
        changeButtonName={i18next.t('saveChanges')}
        closeModal={closeModal}
        disabled={allFiles.length > 0 || note.length > 0 || option ? false : true}
        save={updateIbamaInfo}
      />
    </EnhancedDialog>
  );
};
